import PanelContent from "../../lib/panel/panel-content";
import { cn } from "@babackapp/ui/lib/utils";
import { getSocialIconGlyphUrl } from "@babackapp/ui/lib/social-icons";
import { useTranslation } from "react-i18next";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import { getSocialBadgeStyle } from "./social-badge-style";
import type {
  SocialNetworksBlock,
  SocialNetworksColor,
  SocialNetworksVariant,
} from "./types";

// A representative glyph used only for the panel preview.
const PREVIEW_PLATFORM = "instagram";

const VARIANT_OPTIONS: SocialNetworksVariant[] = ["circle", "square"];
const COLOR_OPTIONS: SocialNetworksColor[] = ["default", "primary", "secondary"];

function SettingsCard({
  selected,
  onSelect,
  name,
  label,
  preview,
}: {
  selected: boolean;
  onSelect: () => void;
  name: string;
  label: string;
  preview: React.ReactNode;
}) {
  return (
    <div className="bg-muted/10 rounded-xl p-0 border border-border/50 flex flex-row overflow-hidden cursor-pointer">
      <label className="flex items-center space-x-3 cursor-pointer w-full">
        <div className="flex items-center space-x-3 cursor-pointer w-full p-3">
          <input
            type="radio"
            name={name}
            checked={selected}
            onChange={onSelect}
            className="sr-only"
          />
          <div
            className={cn(
              "w-4 h-4 rounded-full border-2 flex items-center justify-center",
              selected ? "border-primary bg-primary" : "border-muted-foreground",
            )}
          >
            {selected && (
              <div className="w-2 h-2 rounded-full bg-primary-foreground" />
            )}
          </div>
          <div className="flex-1 font-medium text-xs">{label}</div>
        </div>
        <div className="ml-auto h-full flex items-center pr-3 shrink-0">{preview}</div>
      </label>
    </div>
  );
}

function IconBadgePreview({
  variant,
  color,
}: {
  variant: SocialNetworksVariant;
  color: SocialNetworksColor;
}) {
  const { theme, section } = useBlockPanelPropsContext();
  const badge = getSocialBadgeStyle(theme, section, variant, color);

  return (
    <span
      className={cn("shrink-0", badge.borderRadiusClass)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: badge.backgroundColor,
        padding: "5px",
        lineHeight: 0,
      }}
    >
      {/* craftsman-ignore: TS003 — static hosted preview asset */}
      <img
        src={getSocialIconGlyphUrl(PREVIEW_PLATFORM, badge.glyphColor)}
        alt=""
        width={16}
        height={16}
        style={{
          display: "block",
          flexShrink: 0,
          width: "16px",
          height: "16px",
          maxWidth: "16px",
          objectFit: "contain",
        }}
      />
    </span>
  );
}

export function SocialNetworksEmailPanelStyle() {
  const { t } = useTranslation("editor-blocks-library");
  const blockProps = useBlockPanelPropsContext();
  const { formAPI } = blockProps;
  const block = blockProps.block as SocialNetworksBlock;

  const variant = block.properties?.variant || "circle";
  const color = block.properties?.color || "default";

  const updateProperty = (
    key: "variant" | "color",
    value: SocialNetworksVariant | SocialNetworksColor,
  ) => {
    formAPI.updateField(`${formAPI.blockPath}.properties.${key}`, value);
  };

  const variantLabel: Record<SocialNetworksVariant, string> = {
    circle: t("editorBlocksLibrary.blocks.social-networks.shape.circle", "Circle"),
    square: t("editorBlocksLibrary.blocks.social-networks.shape.square", "Square"),
  };

  // Reuse the button variant labels, already translated in every locale.
  const colorLabel: Record<SocialNetworksColor, string> = {
    default: t("editorBlocksLibrary.blocks.button.variant.default.title", "Default"),
    primary: t("editorBlocksLibrary.blocks.button.variant.primary.title", "Primary"),
    secondary: t("editorBlocksLibrary.blocks.button.variant.secondary.title", "Secondary"),
  };

  return (
    <>
      <PanelContent
        title={t("editorBlocksLibrary.blocks.social-networks.shape.title", "Shape")}
      >
        <div className="grid grid-cols-1 gap-3 w-full">
          {VARIANT_OPTIONS.map((option) => (
            <SettingsCard
              key={option}
              name="social-variant"
              label={variantLabel[option]}
              selected={variant === option}
              onSelect={() => updateProperty("variant", option)}
              preview={<IconBadgePreview variant={option} color={color} />}
            />
          ))}
        </div>
      </PanelContent>

      <PanelContent
        title={t("editorBlocksLibrary.blocks.social-networks.color.title", "Color")}
      >
        <div className="grid grid-cols-1 gap-3 w-full">
          {COLOR_OPTIONS.map((option) => (
            <SettingsCard
              key={option}
              name="social-color"
              label={colorLabel[option]}
              selected={color === option}
              onSelect={() => updateProperty("color", option)}
              preview={<IconBadgePreview variant={variant} color={option} />}
            />
          ))}
        </div>
      </PanelContent>
    </>
  );
}
